/**
 * Portfolio page — positions table + performance chart + theses list.
 * Composes existing components into a single scrollable page.
 *
 * v0.5.7 AE-098: SIM and LIVE are different products. The Detail tab
 * defaults to the ACTIVE mode's ledger — LIVE view is "your money"
 * (real Schwab positions, no SIM noise); SIM view is "the lab" (the
 * strategy proving ground). An explicit toggle (LIVE / SIM / Both)
 * replaces the always-mixed table; `?ledger=` deeplinks it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PerformancePane } from '../PerformancePane';
import { ThesesPane } from '../ThesesPane';
import { ThesisPerformanceChart } from '../ThesisPerformanceChart';
import { CorrelationHeatmap } from '../CorrelationHeatmap';
import { PortfolioContribution } from '../PortfolioContribution';
import { PortfolioBoardPane } from '../PortfolioBoardPane';
import { TaxPane } from '../TaxPane';
import { CalibrationPane } from '../CalibrationPane';
import { useAuth } from '../../lib/auth';
import { useFocal } from '../../lib/focal';
import { PageHeader, PAGE_MAX_W } from '../PageHeader';
import { SchwabConnectionBanner } from '../SchwabConnectionBanner';
import { Sparkline } from '../viz/Sparkline';

type PortfolioView = 'detail' | 'board' | 'tax' | 'calibration';
type LedgerView = 'live' | 'sim' | 'both';

export function PortfolioPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focal = useFocal();
  const { me } = useAuth();

  // v0.5.7 AE-098: which ledger the Detail tab shows. Defaults to the
  // user's ACTIVE mode; `?ledger=live|sim|both` deeplinks/persists it.
  const ledgerParam = searchParams.get('ledger');
  const ledger: LedgerView =
    ledgerParam === 'live' || ledgerParam === 'sim' || ledgerParam === 'both'
      ? ledgerParam
      : me?.mode === 'live'
        ? 'live'
        : 'sim';
  function setLedger(next: LedgerView) {
    const sp = new URLSearchParams(searchParams);
    sp.set('ledger', next);
    setSearchParams(sp, { replace: true });
  }

  // Tab state is reflected in the URL so refresh, back-button, and external
  // deeplinks all behave correctly. `/portfolio` -> detail (default);
  // `/portfolio?view=board` -> the Board allocation pane (v0.5.3 AE-023).
  const viewParam = searchParams.get('view');
  const view: PortfolioView =
    viewParam === 'board'
      ? 'board'
      : viewParam === 'tax'
        ? 'tax'
        : viewParam === 'calibration'
          ? 'calibration'
          : 'detail';
  function setView(next: PortfolioView) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'detail') {
      sp.delete('view');
    } else {
      sp.set('view', next);
    }
    setSearchParams(sp, { replace: true });
  }

  // Deeplink: `/portfolio?symbol=NVDA` -> open the chart FocalCard for NVDA.
  // The row-click navigation already pushes this URL; reading the same
  // param here closes the loop (and supports external `/portfolio?symbol=`
  // entries from chat, the morning email, etc.). v0.5.3 AE-040.
  useEffect(() => {
    const symbol = searchParams.get('symbol');
    if (!symbol) return;
    focal.set({ kind: 'chart', symbol: symbol.toUpperCase() });
    const next = new URLSearchParams(searchParams);
    next.delete('symbol');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, focal]);

  // v0.5.4 AE-066: Deeplink `/portfolio?view=theses` previously landed on
  // generic detail with no indication of where the theses live. Now we
  // scroll the ThesesPane section into view so the user lands ON their
  // theses. URL param is consumed so back-button doesn't re-trigger.
  // v0.5.5 AE-079: `/portfolio?view=theses&thesis_id=…` additionally pops
  // the FocalCard thesis_detail overlay for that thesis so the
  // post-action "View in Portfolio" CTA from BuyThesisDialog lands on
  // the actual artifact.
  const thesesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (viewParam !== 'theses') return;
    const tid = window.setTimeout(() => {
      thesesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    const targetThesis = searchParams.get('thesis_id');
    if (targetThesis) {
      focal.set({ kind: 'thesis_detail', thesisId: targetThesis });
    }
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    next.delete('thesis_id');
    setSearchParams(next, { replace: true });
    return () => window.clearTimeout(tid);
  }, [viewParam, searchParams, setSearchParams, focal]);

  const [ledgers, setLedgers] = useState<api.DualLedgerAccount | null>(null);
  const [positions, setPositions] = useState<api.PositionPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [thesisIds, setThesisIds] = useState<string[]>([]);
  const [thesisNames, setThesisNames] = useState<Record<string, string>>({});

  useEffect(() => {
    api.engineer.listTheses()
      .then((r) => {
        const ids = r.theses.filter((t) => t.status === 'open').map((t) => t.id);
        const names: Record<string, string> = {};
        for (const t of r.theses) names[t.id] = t.symbol || t.id;
        setThesisIds(ids);
        setThesisNames(names);
      })
      .catch(() => {});
  }, []);

  const fetchPortfolio = () => {
    setLoading(true);
    Promise.all([
      api.portfolio.ledgers().catch(() => null),
      api.portfolio.positions('combined').catch(() => [] as api.PositionPublic[]),
    ]).then(([l, p]) => {
      setLedgers(l);
      // v0.5.5 dual-ledger: sort SIM first (default below cards), live next,
      // alphabetical within each. Operators commented in the v0.5.5 audit that
      // they want both ledgers visible without re-sort gymnastics.
      const sorted = [...p].sort((a, b) => {
        if ((a.mode ?? 'sim') !== (b.mode ?? 'sim')) {
          return (a.mode ?? 'sim') === 'live' ? -1 : 1;
        }
        return a.symbol.localeCompare(b.symbol);
      });
      setPositions(sorted);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPortfolio(); }, []);

  const handleRefresh = async () => {
    setSyncing(true);
    try {
      await api.portfolio.sync();
      fetchPortfolio();
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className={`space-y-6 ${PAGE_MAX_W} mx-auto`}>
      <PageHeader
        title="Portfolio"
        subtitle="Positions, performance, and theses for your account."
        badge={<SchwabConnectionBanner compact />}
        actions={
          <>
            <div className="flex rounded overflow-hidden border border-edge" role="tablist">
              <button
                role="tab"
                aria-selected={view === 'detail'}
                data-testid="tab-detail"
                onClick={() => setView('detail')}
                className={`text-xs px-3 py-1.5 ${
                  view === 'detail' ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
                }`}
              >
                Detail
              </button>
              <button
                role="tab"
                aria-selected={view === 'board'}
                data-testid="tab-board"
                onClick={() => setView('board')}
                className={`text-xs px-3 py-1.5 border-l border-edge ${
                  view === 'board' ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
                }`}
              >
                Board
              </button>
              <button
                role="tab"
                aria-selected={view === 'tax'}
                data-testid="tab-tax"
                onClick={() => setView('tax')}
                className={`text-xs px-3 py-1.5 border-l border-edge ${
                  view === 'tax' ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
                }`}
              >
                Tax
              </button>
              <button
                role="tab"
                aria-selected={view === 'calibration'}
                data-testid="tab-calibration"
                onClick={() => setView('calibration')}
                className={`text-xs px-3 py-1.5 border-l border-edge ${
                  view === 'calibration' ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
                }`}
              >
                Calibration
              </button>
            </div>
            <button
              onClick={handleRefresh}
              disabled={syncing}
              className="text-xs px-3 py-1.5 rounded bg-bg-elev hover:bg-edge transition disabled:opacity-50"
              data-testid="refresh-portfolio"
            >
              {syncing ? 'Syncing...' : 'Refresh'}
            </button>
          </>
        }
      />

      <SchwabConnectionBanner />

      {view === 'board' ? (
        <PortfolioBoardPane />
      ) : view === 'tax' ? (
        <TaxPane />
      ) : view === 'calibration' ? (
        <CalibrationPane />
      ) : (
        <PortfolioDetail
          ledgers={ledgers}
          positions={positions}
          loading={loading}
          thesisIds={thesisIds}
          thesisNames={thesisNames}
          navigate={navigate}
          thesesRef={thesesRef}
          ledger={ledger}
          onLedgerChange={setLedger}
        />
      )}
    </div>
  );
}

/** v0.5.7 AE-098: framing per ledger view — LIVE is "your money",
 * SIM is "the lab". The copy is the product story, not decoration. */
const LEDGER_FRAMING: Record<LedgerView, { title: string; blurb: string }> = {
  live: {
    title: 'Your money',
    blurb: 'Real Schwab positions, lots, and tax. Every number here is your actual account.',
  },
  sim: {
    title: 'The lab',
    blurb: 'The proving ground — strategies and theses audition here in paper before anything graduates to LIVE.',
  },
  both: {
    title: 'Combined view',
    blurb: 'Both ledgers side by side. Mode pills mark which rows are real money.',
  },
};

function LedgerToggle({
  ledger,
  onChange,
}: {
  ledger: LedgerView;
  onChange: (next: LedgerView) => void;
}) {
  const opts: { id: LedgerView; label: string }[] = [
    { id: 'live', label: 'LIVE' },
    { id: 'sim', label: 'SIM' },
    { id: 'both', label: 'Both' },
  ];
  return (
    <div className="flex rounded overflow-hidden border border-edge" role="tablist" data-testid="ledger-toggle">
      {opts.map((o, i) => (
        <button
          key={o.id}
          role="tab"
          aria-selected={ledger === o.id}
          data-testid={`ledger-${o.id}`}
          onClick={() => onChange(o.id)}
          className={`text-xs px-3 py-1.5 ${i > 0 ? 'border-l border-edge' : ''} ${
            ledger === o.id ? 'bg-edge text-ink' : 'bg-bg-elev text-ink-mute hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PortfolioDetail({
  ledgers,
  positions,
  loading,
  thesisIds,
  thesisNames,
  navigate,
  thesesRef,
  ledger,
  onLedgerChange,
}: {
  ledgers: api.DualLedgerAccount | null;
  positions: api.PositionPublic[];
  loading: boolean;
  thesisIds: string[];
  thesisNames: Record<string, string>;
  navigate: (to: string) => void;
  thesesRef: React.RefObject<HTMLDivElement | null>;
  ledger: LedgerView;
  onLedgerChange: (next: LedgerView) => void;
}) {
  const liveAvailable = !!(ledgers?.live && ledgers.live.available);
  const noLedgerAtAll =
    !loading &&
    !ledgers &&
    positions.length === 0;
  // v0.5.7 AE-098: the Detail tab shows ONE ledger by default (the
  // active mode's); "Both" restores the mixed v0.5.5 view.
  const shownPositions =
    ledger === 'both'
      ? positions
      : positions.filter((p) => (p.mode ?? 'sim') === ledger);
  const framing = LEDGER_FRAMING[ledger];

  // v0.5.8 AE-106: one 5-day close series per DISTINCT symbol, fetched in
  // a single batch when the symbol set changes (capped at the 12 largest
  // by market value; never refetched per render). Missing history means
  // that row simply has no sparkline.
  const [sparks, setSparks] = useState<Record<string, number[]>>({});
  const sparkSymbols = useMemo(() => {
    const byValue = new Map<string, number>();
    for (const p of shownPositions) {
      const mv = p.market_value ?? p.quantity * p.market_price;
      byValue.set(p.symbol, Math.max(byValue.get(p.symbol) ?? 0, mv));
    }
    return [...byValue.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([s]) => s)
      .sort();
  }, [shownPositions]);
  const sparkKey = sparkSymbols.join(',');
  useEffect(() => {
    if (!sparkKey) return;
    let alive = true;
    Promise.all(
      sparkKey.split(',').map((s) =>
        api.market
          .history(s, '5d', '1d')
          .then((r) => [s, (r.bars ?? []).map((b) => b.close)] as const)
          .catch(() => [s, [] as number[]] as const),
      ),
    ).then((entries) => {
      if (alive) setSparks((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => {
      alive = false;
    };
  }, [sparkKey]);

  // Weight bars: each position's share of the SHOWN ledger's market value.
  const totalShownValue = shownPositions.reduce(
    (acc, p) => acc + (p.market_value ?? p.quantity * p.market_price),
    0,
  );
  return (
    <>
      {/* Disconnect / source banner */}
      {noLedgerAtAll && (
        <div className="card p-3 border-warn bg-bg-elev" data-testid="portfolio-unavailable-banner">
          <div className="text-sm font-semibold text-warn">Portfolio data unavailable</div>
          <div className="text-[11px] text-ink-mute mt-0.5">
            Neither the SIM nor LIVE ledger returned data. If you haven't connected Schwab yet,
            do so in <a href="/settings?tab=brokerage" className="underline">Settings → Brokerage</a>.
            If you have, the connection may have dropped — try Refresh.
          </div>
        </div>
      )}

      {/* v0.5.7 AE-098: ledger framing + toggle. LIVE = your money,
          SIM = the lab. */}
      <div className="card p-3 flex flex-wrap items-center justify-between gap-2" data-testid="ledger-framing">
        <div>
          <div className="text-sm font-semibold">
            {framing.title}{' '}
            {ledger !== 'both' && <ModePill mode={ledger} />}
          </div>
          <div className="text-[11px] text-ink-mute mt-0.5">{framing.blurb}</div>
        </div>
        <LedgerToggle ledger={ledger} onChange={onLedgerChange} />
      </div>

      {/* NAV cards: single-ledger views show their own card; "Both"
          restores the v0.5.5 three-card layout (combined headline). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ledger-cards">
        {ledger === 'both' && (
          <LedgerCard
            label="Total NAV"
            accent="ok"
            nav={ledgers?.combined_nav ?? null}
            subtitle="SIM + LIVE combined"
          />
        )}
        {(ledger === 'live' || ledger === 'both') && (
          <LedgerCard
            label="LIVE · Schwab"
            accent="live"
            nav={liveAvailable ? (ledgers?.live?.nav ?? null) : null}
            cash={liveAvailable ? (ledgers?.live?.cash ?? null) : null}
            positions={liveAvailable ? (ledgers?.live?.positions_count ?? 0) : null}
            subtitle={
              liveAvailable
                ? 'Real Schwab account'
                : ledgers?.live?.error
                  ? `Unavailable — ${ledgers.live.error}`
                  : 'No Schwab account connected'
            }
          />
        )}
        {(ledger === 'sim' || ledger === 'both') && (
          <LedgerCard
            label="Paper · SIM"
            accent="sim"
            nav={ledgers?.sim?.nav ?? null}
            cash={ledgers?.sim?.cash ?? null}
            positions={ledgers?.sim?.positions_count ?? 0}
            subtitle="Persisted simulated ledger"
          />
        )}
      </div>

      {/* Performance chart */}
      <PerformancePane />

      {/* Positions table */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold mb-3">
          Positions ({shownPositions.length}
          {ledger !== 'both' && ` · ${ledger.toUpperCase()}`})
        </h3>
        {loading && <div className="text-xs text-ink-mute">Loading...</div>}
        {!loading && shownPositions.length === 0 && (
          <div className="text-xs text-ink-mute">
            {ledger === 'both'
              ? 'No open positions in either ledger.'
              : ledger === 'live'
                ? 'No LIVE positions. Connect Schwab in Settings → Brokerage, or switch to SIM to see the lab.'
                : 'No SIM positions yet — the lab is empty. Buy a thesis in SIM from Research to start an experiment.'}
          </div>
        )}
        {!loading && shownPositions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-ink-mute text-left">
                <tr>
                  <th className="pb-1">Symbol</th>
                  <th className="pb-1">Mode</th>
                  <th className="pb-1">5d</th>
                  <th className="pb-1 text-right">Qty</th>
                  <th className="pb-1 text-right">Avg</th>
                  <th className="pb-1 text-right">Last</th>
                  <th className="pb-1 text-right">Value</th>
                  <th className="pb-1 text-right">Weight</th>
                  <th className="pb-1 text-right">P&L</th>
                  <th className="pb-1 text-right">Research</th>
                </tr>
              </thead>
              <tbody>
                {shownPositions.map((p) => {
                  const mv = p.market_value ?? p.quantity * p.market_price;
                  const cost = p.quantity * p.avg_price;
                  const pnl = mv - cost;
                  const mode = p.mode ?? 'sim';
                  const closes = sparks[p.symbol] ?? [];
                  const weightPct = totalShownValue > 0 ? (100 * mv) / totalShownValue : null;
                  return (
                    <tr
                      key={`${mode}:${p.symbol}`}
                      className="border-t border-edge cursor-pointer hover:bg-bg-elev"
                      onClick={() => navigate(`/portfolio?symbol=${p.symbol}`)}
                      data-testid={`position-row-${mode}-${p.symbol}`}
                    >
                      <td className="py-1.5 font-semibold">{p.symbol}</td>
                      <td className="py-1.5">
                        <ModePill mode={mode} />
                      </td>
                      <td className="py-1.5" data-testid={`position-spark-${mode}-${p.symbol}`}>
                        {closes.length >= 2 ? (
                          <Sparkline values={closes} width={48} height={16} dot={false} strokeWidth={1} aria-label={`${p.symbol} 5-day trend`} />
                        ) : null}
                      </td>
                      <td className="py-1.5 text-right num">{p.quantity}</td>
                      <td className="py-1.5 text-right num">${p.avg_price.toFixed(2)}</td>
                      <td className="py-1.5 text-right num">${p.market_price.toFixed(2)}</td>
                      <td className="py-1.5 text-right num">${mv.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="py-1.5 text-right" data-testid={`position-weight-${mode}-${p.symbol}`}>
                        {weightPct == null ? null : (
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            <span className="inline-block h-1.5 w-12 rounded-sm bg-edge overflow-hidden">
                              <span
                                className="block h-full bg-accent"
                                style={{ width: `${Math.min(100, weightPct).toFixed(1)}%` }}
                              />
                            </span>
                            <span className="num text-ink-mute">{weightPct.toFixed(0)}%</span>
                          </span>
                        )}
                      </td>
                      <td className={`py-1.5 text-right num ${pnl >= 0 ? 'text-ok' : 'text-bad'}`}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                      </td>
                      <td className="py-1.5 text-right">
                        <button
                          className="text-[10px] underline text-ink-mute hover:text-ink"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/research?q=${p.symbol}`);
                          }}
                          data-testid={`research-link-${p.symbol}`}
                        >
                          research
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Thesis Performance Chart */}
      {thesisIds.length > 0 && (
        <ThesisPerformanceChart thesisIds={thesisIds} thesisNames={thesisNames} />
      )}

      {/* Portfolio Contribution */}
      {thesisIds.length > 0 && (
        <PortfolioContribution thesisIds={thesisIds} thesisNames={thesisNames} />
      )}

      {/* Cross-Thesis Correlation Heatmap */}
      <CorrelationHeatmap autoLoad={true} />

      {/* Theses (deeplink target for view=theses, AE-066) */}
      <div ref={thesesRef} data-testid="theses-section">
        <h3 className="text-sm font-semibold mb-3">Theses</h3>
        <ThesesPane />
      </div>
    </>
  );
}

function LedgerCard({
  label,
  accent,
  nav,
  cash,
  positions,
  subtitle,
}: {
  label: string;
  accent: 'ok' | 'live' | 'sim';
  nav: number | null;
  cash?: number | null;
  positions?: number | null;
  subtitle?: string;
}) {
  const fmtUsd = (v: number | null | undefined) =>
    v == null ? '—' : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const accentCls =
    accent === 'live'
      ? 'border-l-2 border-l-ok'
      : accent === 'sim'
        ? 'border-l-2 border-l-edge'
        : 'border-l-2 border-l-accent';
  return (
    <div
      className={`card p-3 ${accentCls}`}
      data-testid={`ledger-card-${accent}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] text-ink-mute uppercase tracking-wide">{label}</div>
        {accent !== 'ok' && <ModePill mode={accent === 'live' ? 'live' : 'sim'} />}
      </div>
      <div className="num text-2xl font-bold mt-1">{fmtUsd(nav)}</div>
      {(cash !== undefined || positions !== undefined) && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] text-ink-mute">
          {cash !== undefined && (
            <div>
              <div>Cash</div>
              <div className="num text-ink">{fmtUsd(cash)}</div>
            </div>
          )}
          {positions !== undefined && positions !== null && (
            <div>
              <div>Positions</div>
              <div className="num text-ink">{positions}</div>
            </div>
          )}
        </div>
      )}
      {subtitle && <div className="text-[10px] text-ink-mute mt-2">{subtitle}</div>}
    </div>
  );
}

function ModePill({ mode }: { mode: 'sim' | 'live' }) {
  if (mode === 'live') {
    return (
      <span
        className="inline-block text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-ok/15 text-ok"
        data-testid="mode-pill-live"
      >
        live
      </span>
    );
  }
  return (
    <span
      className="inline-block text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-edge text-ink-mute"
      data-testid="mode-pill-sim"
    >
      paper
    </span>
  );
}
